import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { CurrencyGbp, FloppyDisk, ArrowCounterClockwise, Warning, Star, X, ArrowRight, TrendUp, TrendDown } from "@phosphor-icons/react";

export default function AdminPricing() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setCfg((await api.get("/admin/pricing")).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load pricing"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const amf = cfg?.annual_months_free ?? 2;
  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setGP = (k, v) => setCfg((c) => ({ ...c, growth_pack: { ...c.growth_pack, [k]: v } }));
  const setPlan = (id, k, v) => setCfg((c) => ({
    ...c, plans: c.plans.map((p) => (p.id === id ? { ...p, [k]: v } : p)),
  }));

  const buildPayload = () => ({
    annual_months_free: Number(cfg.annual_months_free),
    enterprise_threshold: Number(cfg.enterprise_threshold),
    max_growth_packs: Number(cfg.max_growth_packs),
    growth_pack: { companies: Number(cfg.growth_pack.companies), monthly: Number(cfg.growth_pack.monthly) },
    plans: cfg.plans.filter((p) => !p.enterprise).map((p) => ({
      id: p.id, name: p.name, companies: Number(p.companies), monthly: Number(p.monthly),
      growth_pack: !!p.growth_pack, featured: !!p.featured,
    })),
  });

  const openPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/pricing/preview", buildPayload());
      setImpact(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not preview impact"); }
    finally { setPreviewing(false); }
  };

  const commitSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/pricing", buildPayload());
      setCfg(data);
      setImpact(null);
      toast.success("Pricing updated everywhere — website, app, discounts & billing.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save pricing"); }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset all prices back to the built-in defaults?")) return;
    setSaving(true);
    try { setCfg((await api.delete("/admin/pricing")).data); toast.success("Pricing reset to defaults."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reset"); }
    finally { setSaving(false); }
  };

  const gbp = (n) => (n == null ? "—" : `£${Number(n).toLocaleString()}`);

  return (
    <AdminLayout>
      <div className="max-w-[1100px] mx-auto px-6 py-8" data-testid="admin-pricing-page">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(242,111,33,0.12)" }}>
              <CurrencyGbp size={22} weight="fill" style={{ color: "#F37A24" }} />
            </span>
            <div>
              <h1 className="font-heading font-black text-navy text-2xl tracking-tight">Pricing</h1>
              <p className="text-navy/70 text-sm">Edit plan prices — changes flow through the website, in-app subscription, discounts and billing instantly.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={resetDefaults} disabled={saving || loading} data-testid="pricing-reset-btn"
              className="inline-flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium rounded-lg border border-slate-200 text-navy/70 hover:bg-slate-50 transition-colors disabled:opacity-50">
              <ArrowCounterClockwise size={16} /> Reset
            </button>
            <button onClick={openPreview} disabled={saving || loading || previewing} data-testid="pricing-save-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg text-white transition-colors disabled:opacity-60"
              style={{ background: "#F37A24" }}>
              <FloppyDisk size={17} weight="bold" /> {previewing ? "Checking impact…" : "Save changes"}
            </button>
          </div>
        </div>

        {loading || !cfg ? (
          <div className="card-surface p-12 text-center text-navy/65">Loading…</div>
        ) : (
          <>
            {cfg.warnings?.length > 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 mb-5" data-testid="pricing-warnings">
                <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-1">
                  <Warning size={18} weight="fill" /> Pricing sanity check
                </div>
                <ul className="list-disc pl-6 text-sm text-amber-900 space-y-1">
                  {cfg.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}

            {/* Plans table */}
            <div className="card-surface p-5 mb-5 overflow-x-auto">
              <table className="w-full text-sm" data-testid="pricing-plans-table">
                <thead>
                  <tr className="text-left text-navy/65 text-xs uppercase tracking-wide">
                    <th className="pb-3 pr-3">Plan</th>
                    <th className="pb-3 px-3">Companies</th>
                    <th className="pb-3 px-3">£ / month</th>
                    <th className="pb-3 px-3">£ / year</th>
                    <th className="pb-3 px-3">Save / yr</th>
                    <th className="pb-3 px-3 text-center">Growth pack</th>
                    <th className="pb-3 px-3 text-center">Featured</th>
                  </tr>
                </thead>
                <tbody>
                  {cfg.plans.map((p) => {
                    const yearly = p.monthly == null ? null : Number(p.monthly) * (12 - amf);
                    const saving = p.monthly == null ? null : Number(p.monthly) * amf;
                    return (
                      <tr key={p.id} className="border-t border-slate-100" data-testid={`plan-row-${p.id}`}>
                        <td className="py-2.5 pr-3">
                          <input value={p.name} onChange={(e) => setPlan(p.id, "name", e.target.value)}
                            data-testid={`plan-name-${p.id}`}
                            className="w-32 rounded-lg border border-slate-200 px-2 py-1.5 text-navy font-medium outline-none focus:border-orange" />
                        </td>
                        {p.enterprise ? (
                          <>
                            <td className="px-3 text-navy/65" colSpan={4}>Custom (contact us)</td>
                          </>
                        ) : (
                          <>
                            <td className="px-3">
                              <input type="number" min="1" value={p.companies}
                                onChange={(e) => setPlan(p.id, "companies", e.target.value)} data-testid={`plan-companies-${p.id}`}
                                className="w-20 rounded-lg border border-slate-200 px-2 py-1.5 outline-none focus:border-orange" />
                            </td>
                            <td className="px-3">
                              <input type="number" min="0" value={p.monthly}
                                onChange={(e) => setPlan(p.id, "monthly", e.target.value)} data-testid={`plan-monthly-${p.id}`}
                                className="w-24 rounded-lg border border-slate-200 px-2 py-1.5 font-semibold outline-none focus:border-orange" />
                            </td>
                            <td className="px-3 text-navy/70 tabular-nums">{gbp(yearly)}</td>
                            <td className="px-3 text-positive font-semibold tabular-nums">{gbp(saving)}</td>
                          </>
                        )}
                        <td className="px-3 text-center">
                          {!p.enterprise && (
                            <input type="checkbox" checked={!!p.growth_pack} onChange={(e) => setPlan(p.id, "growth_pack", e.target.checked)}
                              data-testid={`plan-growthpack-${p.id}`} className="w-4 h-4 accent-orange" />
                          )}
                        </td>
                        <td className="px-3 text-center">
                          {!p.enterprise && (
                            <button onClick={() => setPlan(p.id, "featured", !p.featured)} data-testid={`plan-featured-${p.id}`}
                              className={p.featured ? "text-orange" : "text-slate-300 hover:text-slate-600"}>
                              <Star size={18} weight={p.featured ? "fill" : "regular"} />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Global settings */}
            <div className="grid md:grid-cols-2 gap-5">
              <div className="card-surface p-5">
                <h2 className="font-heading font-bold text-navy mb-3">Growth pack (add-on)</h2>
                <div className="grid grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs text-navy/70">Extra companies</span>
                    <input type="number" min="1" value={cfg.growth_pack.companies} onChange={(e) => setGP("companies", e.target.value)}
                      data-testid="gp-companies" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 outline-none focus:border-orange" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-navy/70">£ / month</span>
                    <input type="number" min="0" value={cfg.growth_pack.monthly} onChange={(e) => setGP("monthly", e.target.value)}
                      data-testid="gp-monthly" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 font-semibold outline-none focus:border-orange" />
                  </label>
                </div>
              </div>
              <div className="card-surface p-5">
                <h2 className="font-heading font-bold text-navy mb-3">Global settings</h2>
                <div className="grid grid-cols-3 gap-3">
                  <label className="block">
                    <span className="text-xs text-navy/70">Free months (annual)</span>
                    <input type="number" min="0" max="11" value={cfg.annual_months_free} onChange={(e) => setField("annual_months_free", e.target.value)}
                      data-testid="cfg-amf" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 outline-none focus:border-orange" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-navy/70">Enterprise at</span>
                    <input type="number" min="2" value={cfg.enterprise_threshold} onChange={(e) => setField("enterprise_threshold", e.target.value)}
                      data-testid="cfg-ent" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 outline-none focus:border-orange" />
                  </label>
                  <label className="block">
                    <span className="text-xs text-navy/70">Max packs</span>
                    <input type="number" min="0" max="10" value={cfg.max_growth_packs} onChange={(e) => setField("max_growth_packs", e.target.value)}
                      data-testid="cfg-maxpacks" className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 outline-none focus:border-orange" />
                  </label>
                </div>
              </div>
            </div>

            {cfg.updated_at && (
              <p className="text-xs text-navy/65 mt-4" data-testid="pricing-updated">
                Last updated {new Date(cfg.updated_at).toLocaleString("en-GB")}{cfg.updated_by ? ` by ${cfg.updated_by}` : ""}.
                {cfg.is_default && " (Currently using built-in defaults.)"}
              </p>
            )}
          </>
        )}
      </div>

      {impact && (
        <div className="fixed inset-0 bg-navy/40 backdrop-blur-sm z-50 flex items-center justify-center px-6 py-8" data-testid="pricing-impact-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-full overflow-y-auto">
            <div className="flex items-start justify-between p-6 pb-4 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl">
              <div>
                <h3 className="font-heading font-bold text-navy text-lg">Confirm pricing changes</h3>
                <p className="text-sm text-navy/70">Review how these prices affect existing subscribers before they go live.</p>
              </div>
              <button onClick={() => setImpact(null)} data-testid="impact-close" className="text-navy/65 hover:text-navy"><X size={20} /></button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="overline text-navy/65 mb-1">Accounts on a plan</div>
                  <div className="font-heading font-black text-2xl text-navy" data-testid="impact-total-accounts">{impact.total_accounts}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="overline text-navy/65 mb-1">Paying subs affected</div>
                  <div className="font-heading font-black text-2xl text-navy" data-testid="impact-subscribed">{impact.subscribed_affected}</div>
                </div>
                <div className="rounded-xl bg-slate-50 p-4">
                  <div className="overline text-navy/65 mb-1">MRR change</div>
                  <div className={`font-heading font-black text-2xl inline-flex items-center gap-1 ${impact.monthly_revenue_delta > 0 ? "text-positive" : impact.monthly_revenue_delta < 0 ? "text-warn" : "text-navy/70"}`} data-testid="impact-mrr-delta">
                    {impact.monthly_revenue_delta > 0 ? <TrendUp size={18} weight="bold" /> : impact.monthly_revenue_delta < 0 ? <TrendDown size={18} weight="bold" /> : null}
                    {impact.monthly_revenue_delta >= 0 ? "+" : "−"}{gbp(Math.abs(impact.monthly_revenue_delta))}
                  </div>
                  <div className="text-xs text-navy/65 mt-0.5">{impact.annual_revenue_delta >= 0 ? "+" : "−"}{gbp(Math.abs(impact.annual_revenue_delta))}/yr</div>
                </div>
              </div>

              {impact.per_plan?.length > 0 && (
                <div>
                  <div className="overline text-navy/65 mb-2">Per plan</div>
                  <div className="rounded-xl border border-slate-100 overflow-hidden">
                    <table className="w-full text-sm" data-testid="impact-per-plan">
                      <thead><tr className="text-left text-navy/65 text-xs uppercase tracking-wide bg-slate-50">
                        <th className="px-4 py-2.5">Plan</th><th className="px-4 py-2.5">Subs</th><th className="px-4 py-2.5">Price change</th>
                      </tr></thead>
                      <tbody>
                        {impact.per_plan.map((p) => (
                          <tr key={p.plan_id} className="border-t border-slate-100">
                            <td className="px-4 py-2.5 font-medium text-navy">{p.name}</td>
                            <td className="px-4 py-2.5 text-navy/70">{p.subscribers}</td>
                            <td className="px-4 py-2.5">
                              {p.changed ? (
                                <span className="inline-flex items-center gap-1.5 text-navy">
                                  <span className="text-navy/70">{gbp(p.old_monthly)}</span>
                                  <ArrowRight size={13} className="text-navy/65" />
                                  <span className="font-semibold">{gbp(p.new_monthly)}</span>
                                </span>
                              ) : <span className="text-navy/65">No change</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {impact.allowance_issues?.length > 0 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="impact-allowance-issues">
                  <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-1">
                    <Warning size={18} weight="fill" /> {impact.allowance_issues.length} account{impact.allowance_issues.length > 1 ? "s" : ""} would exceed their new company allowance
                  </div>
                  <ul className="list-disc pl-6 text-sm text-amber-900 space-y-0.5">
                    {impact.allowance_issues.map((a, i) => (
                      <li key={i}>{a.name} — {a.used} in use, new allowance {a.new_allowance} ({a.plan})</li>
                    ))}
                  </ul>
                </div>
              )}

              {impact.warnings?.length > 0 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="impact-warnings">
                  <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm mb-1"><Warning size={18} weight="fill" /> Pricing sanity check</div>
                  <ul className="list-disc pl-6 text-sm text-amber-900 space-y-0.5">{impact.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}

              {impact.total_accounts === 0 && (
                <p className="text-sm text-navy/70">No existing accounts are on a standard plan yet — this change is safe to apply.</p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 p-6 pt-0">
              <button onClick={() => setImpact(null)} data-testid="impact-cancel"
                className="px-4 py-2.5 text-sm font-medium rounded-lg border border-slate-200 text-navy/70 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button onClick={commitSave} disabled={saving} data-testid="impact-confirm"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg text-white transition-colors disabled:opacity-60"
                style={{ background: "#F37A24" }}>
                <FloppyDisk size={17} weight="bold" /> {saving ? "Saving…" : "Confirm & save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
